// Public vanity counter for /get/<slug> smart-link page views.
//
// The Firestore doc lives at smartLinkStats/<slug> with shape:
//   { clicks: number, updatedAt: Timestamp }
//
// Security rules (see firestore.rules) only allow:
//   - public read
//   - create with clicks=1
//   - update where clicks increments by exactly 1
// This protects against tampering while keeping the counter unauthenticated.
//
// Session debounce: we use sessionStorage so a single browser session counts
// once. Refreshes within the same tab don't inflate. Closing the tab resets,
// which is acceptable for a vanity counter.

import { doc, getDoc, increment, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

const STORAGE_KEY = 'smart-link-counted';

function getCountedSet(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
}

function markCounted(slug: string) {
  if (typeof window === 'undefined') return;
  try {
    const set = getCountedSet();
    set.add(slug);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
  } catch {
    /* sessionStorage may be unavailable (e.g. private mode) — ignore */
  }
}

/** Increment the counter once per session, then return the current count.
 *  Returns null if Firestore is unreachable (caller should hide the badge). */
export async function recordSmartLinkClick(slug: string): Promise<number | null> {
  const ref = doc(db, 'smartLinkStats', slug);
  const counted = getCountedSet().has(slug);

  try {
    if (counted) {
      // Already incremented this session — just read.
      const snap = await getDoc(ref);
      return snap.exists() ? (snap.data().clicks as number) : null;
    }

    // First view in this session — increment then read.
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      await setDoc(ref, { clicks: 1, updatedAt: serverTimestamp() });
      markCounted(slug);
      return 1;
    }

    await updateDoc(ref, { clicks: increment(1), updatedAt: serverTimestamp() });
    markCounted(slug);
    const next = await getDoc(ref);
    return next.exists() ? (next.data().clicks as number) : null;
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[smartLinkStats] failed:', err);
    }
    return null;
  }
}

/** Format a click count for the dot-matrix display. Pads to 5 digits, no
 *  separator (matrix font has no comma glyph). 99,999+ shows as "99999+". */
export function formatClickCount(n: number): string {
  if (n > 99999) return '99999';
  return String(n).padStart(5, '0');
}
